import { Pipe, PipeTransform } from '@angular/core';

import { Cloudinary } from '@cloudinary/url-gen';
import Transformation from '@cloudinary/url-gen/backwards/transformation';

import { Seo } from '../data/models/components/seo';
import { UrlService } from './url.service';

@Pipe({
  name: 'seoImageUrl',
})
export class SeoImageUrlPipe implements PipeTransform {
  constructor(
    private cloudinary: Cloudinary,
    private urlService: UrlService
  ) {}

  transform(
    seo: Seo,
    options?: {
      text: boolean;
      format: 'square' | 'rectangle';
      logo: boolean;
      category: string;
    }
  ): string {
    options = {
      ...{
        text: false,
        size: 'md',
        format: 'rectangle',
        logo: false,
        category: '',
      },
      ...options,
    };
    const HEIGHT = 630;
    const WIDTH = options.format === 'square' ? HEIGHT : 1200;
    const FILLER_WIDTH = 60;
    const TEXT_MARGIN = FILLER_WIDTH + 80;
    const TEXT_WIDTH = WIDTH - 2 * TEXT_MARGIN;
    const ICON_SIZE = 100;
    const ICON_MARGIN = 10;
    const LOGO_WIDTH = 120;

    // TODO agency secondary color source
    const background = '#363583';
    const image = this.cloudinary
      .image(this.urlService.parsePublicId(seo.image.url))
      .format('jpg')
      .addTransformation(
        new Transformation({
          background,
          opacity: 40,
          height: HEIGHT,
          width: WIDTH,
          crop: 'fill',
        }).toString()
      )
      .addTransformation(
        new Transformation({
          background,
          color: background,
          opacity: 100,
          gravity: 'west',
          height: HEIGHT,
          width: FILLER_WIDTH,
          overlay: { text: '.', font_family: 'Arial', font_size: 1 },
          crop: 'thumb',
        }).toString()
      );

    if (options.logo) {
      // TODO agency sticky logo source
      const logo = 'medium_logo_white_d1cd1d6c1a';
      image.addTransformation(
        new Transformation({
          width: LOGO_WIDTH,
          height: this.urlService.logoHeight(LOGO_WIDTH),
          gravity: 'south_east',
          overlay: logo,
          crop: 'fill',
          x: 20,
          y: 20,
        }).toString()
      );
    }

    if (options.text) {
      if (options.category) {
        image.addTransformation(
          new Transformation({
            overlay: {
              text: this.urlService.encode(options.category),
              font_family: 'Roboto%20Condensed',
              font_size: 80,
              font_weight: 'bold',
            },
            gravity: 'north_west',
            x: TEXT_MARGIN,
            y: 70,
            width: TEXT_WIDTH,
            height: 100,
            crop: 'fit',
            opacity: 20,
            color: 'white',
          }).toString()
        );
      }
      image
        .addTransformation(
          new Transformation({
            overlay: {
              text: this.urlService.encode(seo.title),
              font_family: 'Roboto%20Condensed',
              font_size: 60,
              font_weight: 'bold',
            },
            gravity: 'south_west',
            x: TEXT_MARGIN,
            y: 170,
            width: TEXT_WIDTH,
            height: 200,
            crop: 'fit',
            color: 'white',
          }).toString()
        )
        .addTransformation(
          new Transformation({
            overlay: {
              text: this.urlService.encode(seo.description),
              font_family: 'Roboto%20Condensed',
              font_size: 40,
              font_weight: 'light',
            },
            gravity: 'north_west',
            x: TEXT_MARGIN,
            y: 470,
            width: TEXT_WIDTH,
            height: 150,
            crop: 'fit',
            color: 'white',
          }).toString()
        );
    }

    (seo.icons || []).forEach((icon, index) => {
      let size, x, y: number;
      if (index === 0) {
        size = ICON_SIZE;
        x = ICON_MARGIN;
        y = ICON_SIZE / 2;
      } else {
        size = ICON_SIZE / 2;
        x = ICON_MARGIN + size / 2;
        y = ICON_SIZE + index * (size / 2 + ICON_MARGIN * 2);
      }

      image.addTransformation(
        new Transformation({
          width: size,
          height: size,
          gravity: 'north_west',
          overlay: this.urlService.parsePublicId(icon.url),
          crop: 'pad',
          radius: '100',
          x,
          y,
          color: 'white',
        }).toString()
      );
    });

    return decodeURI(image.toURL());
  }
}
